import { keepPreviousData, useQuery } from '@tanstack/react-query';
import service from '../service';
import type { QuickMessageMethod } from '../types/messageTemplate';

type MergeTagOption = { id: string; label: string };

const QUICK_MESSAGE_STALE_MS = 30_000;

export const quickMessageQueryKeys = {
  all: ['quickMessages'] as const,
  list: (method: QuickMessageMethod, q: string) =>
    [...quickMessageQueryKeys.all, 'list', method, q] as const,
  categories: () => [...quickMessageQueryKeys.all, 'categories'] as const,
  mergeTags: () => [...quickMessageQueryKeys.all, 'mergeTags'] as const,
};

type UseQuickMessagesParams = {
  method: QuickMessageMethod;
  q: string;
  enabled?: boolean;
};

/** Quick messages for one inbox channel, filtered server-side by `q`. */
export function useQuickMessages({
  method,
  q,
  enabled = true,
}: UseQuickMessagesParams) {
  return useQuery({
    queryKey: quickMessageQueryKeys.list(method, q),
    queryFn: () => service.getQuickMessages({ method, q }),
    select: (res) => res.data,
    enabled,
    staleTime: QUICK_MESSAGE_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** Every category in use on a live message template, for the template form. */
export function useQuickMessageCategories({ enabled = true } = {}) {
  return useQuery({
    queryKey: quickMessageQueryKeys.categories(),
    queryFn: () => service.getMessageTemplateCategories(),
    select: (res) => res.data,
    enabled,
  });
}

/**
 * Merge tags an inbox send can fill: fish and subscription tags, the only
 * data the API has for the recipient fish at send time.
 */
export function useQuickMessageMergeTags({ enabled = true } = {}) {
  return useQuery({
    queryKey: quickMessageQueryKeys.mergeTags(),
    queryFn: (): Promise<{ data: MergeTagOption[] }> => service.getMergeTags(),
    select: (res) => res.data,
    enabled,
    staleTime: Infinity,
  });
}
